/*
 * FarmSteady — recipe star-rating controller (build-queue item 8).
 *
 * Loaded (deferred) by snippets/fs-recipe-rating-widget.liquid — recipe
 * articles only, never from theme.liquid. Sends a "Rated Recipe" event to
 * Klaviyo over TWO paths (same metric name, same properties
 * {handle, rating, device_id} — aggregation is uniform across both):
 *
 *   PRIMARY — Klaviyo's own JS present (window.klaviyo, or legacy _learnq):
 *     native track call via the documented push(['track', metric, props])
 *     queue. The event attaches to the visitor's OWN (anonymous-or-known)
 *     profile through Klaviyo's real tracking — DESIRED attribution, per
 *     design review 2026-07-06. Fire-and-forget by design (the queue exposes no
 *     response); the rating is stored locally on dispatch.
 *
 *   FALLBACK — Klaviyo JS absent or blocked: raw fetch to the client
 *     Events API (same proven pattern as assets/farmsteady-bis.js — this
 *     file never loads klaviyo.js itself, perf guardrail):
 *       POST https://a.klaviyo.com/client/events/?company_id={PUBLIC_KEY}
 *       headers: Content-Type: application/json, revision: KLAVIYO_REVISION
 *       Success is 202 Accepted with an empty body.
 *     Events on this path attach to ONE fixed service profile
 *     (external_id "recipe-ratings-widget") — no per-rater profiles →
 *     no billable-profile growth, no PII.
 *
 * DEV-THEME NOTE (verified 2026-07-06): the live site loads klaviyo.js,
 * the dev theme does NOT (Klaviyo loads via per-theme app embed, not
 * ScriptTag). On the dev theme only the fallback fires today; the primary
 * activates once the app embed is enabled at cutover (tracked separately).
 *
 * HARD CONSTRAINT (design review, 2026-07-06): "we don't want to do anything that
 * interferes with actual tracking." Riding the visitor's own tracking via
 * the native track call is desired — but this code must never DISTURB
 * Klaviyo's tracking. Code-level guarantees held here:
 *   1. Never calls any identify mechanism, on either path.
 *   2. Never reads or writes the _kx cookie or any Klaviyo cookie. The
 *      fallback fetch sets credentials:'omit' so no cookie of any kind
 *      rides along cross-origin.
 *   3. Never passes the visitor's email/phone/any identifier — even if
 *      available elsewhere on the page. The fallback payload's profile is
 *      ONLY the fixed service external_id; the primary path passes only
 *      {handle, rating, device_id} event properties and lets Klaviyo's own
 *      tracking decide the profile.
 *   4. Never loads, injects, or reconfigures klaviyo.js — the primary path
 *      only uses a queue Klaviyo's own embed already put on the page.
 *   5. No interaction with the BIS module (fs-bis) or inline-signup forms —
 *      this controller only touches [data-fs-recipe-rating] roots.
 *
 * VERIFY BEFORE TRUST (cutover gate): the first live event must be
 * verified in the Klaviyo account — metric appears; events on visitor
 * profiles are EXPECTED (primary) alongside the service profile (fallback);
 * no new identifiers on any profile, no identify effects, no cookie
 * changes; the same device_id dedupes across a fallback rating and a
 * native rating; no flow references the metric; no billing surprises —
 * before the aggregation script is scheduled. Full checklist:
 * Cowork FarmSteady Web Design/scripts/recipe_ratings_aggregate.py.
 *
 * Rating identity (anonymous by design): device_id is a uuid persisted in
 * localStorage (fs-rr-device-id), sent on BOTH paths. The nightly
 * aggregation keeps only the LATEST rating per (device_id, handle), so
 * re-taps CHANGE your rating — the widget also posts again on re-tap and
 * updates the local rated-handles map (fs-rr-rated) so returning visitors
 * see "You rated this N★ — tap to change." If localStorage is unavailable
 * (private mode / blocked), a per-pageview in-memory id is used: rating
 * still works, re-taps within the page still dedupe, but a later visit
 * counts as a new device. Acceptable — never block rating on storage.
 *
 * Progressive enhancement (farmsteady-recipe-rating.css): the module ships
 * display:none and only appears when this script adds .fs-rr--enhanced, so
 * no-JS visitors never see dead controls (BIS contract style).
 *
 * Accessibility: radiogroup with roving tabindex — Arrow keys move focus
 * between the star radios, Enter/Space (native button behavior) commits.
 */

(function () {
  'use strict';

  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_EVENTS_ENDPOINT = 'https://a.klaviyo.com/client/events/?company_id=';
  const METRIC_NAME = 'Rated Recipe';
  const SERVICE_PROFILE_EXTERNAL_ID = 'recipe-ratings-widget';
  const DEVICE_KEY = 'fs-rr-device-id';
  const RATED_KEY = 'fs-rr-rated';

  const COPY = {
    rated: (n) => 'You rated this ' + n + '★ — tap to change.',
    failure: "Couldn't save your rating — please try again in a minute.",
  };

  const READY_STATES = ['interactive', 'complete'];

  /* ---------------------------------------------------------- storage */

  // localStorage can throw on access (private mode, blocked cookies) —
  // fall back to a per-pageview in-memory store so rating never breaks.
  const memoryStore = {};
  let storageOk = null;

  function storageAvailable() {
    if (storageOk !== null) return storageOk;
    try {
      const probe = '__fs-rr-probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      storageOk = true;
    } catch (error) {
      storageOk = false;
    }
    return storageOk;
  }

  function storeGet(key) {
    if (storageAvailable()) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        /* fall through */
      }
    }
    return Object.prototype.hasOwnProperty.call(memoryStore, key) ? memoryStore[key] : null;
  }

  function storeSet(key, value) {
    if (storageAvailable()) {
      try {
        window.localStorage.setItem(key, value);
        return;
      } catch (error) {
        /* fall through */
      }
    }
    memoryStore[key] = value;
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      return (
        hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) +
        '-' + hex.slice(16, 20) + '-' + hex.slice(20)
      );
    }
    // Last-resort fallback; only reached on museum-grade browsers.
    return 'fs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function deviceId() {
    let id = storeGet(DEVICE_KEY);
    if (!id) {
      id = uuid();
      storeSet(DEVICE_KEY, id);
    }
    return id;
  }

  function readRatedMap() {
    try {
      const raw = storeGet(RATED_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function getStoredRating(handle) {
    const entry = readRatedMap()[handle];
    const value = entry && Number(entry.rating);
    return value >= 1 && value <= 5 ? value : null;
  }

  function setStoredRating(handle, rating) {
    const map = readRatedMap();
    map[handle] = { rating: rating, at: new Date().toISOString() };
    try {
      storeSet(RATED_KEY, JSON.stringify(map));
    } catch (error) {
      /* non-fatal — the event was already dispatched to Klaviyo */
    }
  }

  /* --------------------------------------------------------- transport */

  // PRIMARY path detection: Klaviyo's own embed exposes window.klaviyo
  // (modern) or window._learnq (legacy). Both accept the documented
  // push(['track', metric, properties]) form — including before the full
  // script finishes loading (stub queue). We only USE a queue Klaviyo
  // already put on the page; we never create or load one.
  function nativeKlaviyoQueue() {
    const queue = window.klaviyo || window._learnq;
    return queue && typeof queue.push === 'function' ? queue : null;
  }

  function eventProperties(handle, rating) {
    // Identical property shape on both paths — the nightly aggregation
    // dedupes on (device_id, handle) regardless of which path fired.
    return {
      handle: handle,
      rating: rating,
      device_id: deviceId(),
    };
  }

  // FALLBACK payload (Klaviyo client Events API). profile is ONLY the fixed
  // service external_id — never a visitor identifier (hard constraint; see
  // header).
  function buildFallbackPayload(handle, rating) {
    return {
      data: {
        type: 'event',
        attributes: {
          properties: eventProperties(handle, rating),
          metric: {
            data: {
              type: 'metric',
              attributes: { name: METRIC_NAME },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: { external_id: SERVICE_PROFILE_EXTERNAL_ID },
            },
          },
        },
      },
    };
  }

  /* ---------------------------------------------------------------- UI */

  function setStatus(root, kind, message) {
    const status = root.querySelector('[data-fs-rr-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('fs-rr__status--rated', kind === 'rated');
    status.classList.toggle('fs-rr__status--error', kind === 'error');
  }

  function stars(root) {
    return Array.from(root.querySelectorAll('.fs-rr__star'));
  }

  function reflectRating(root, rating) {
    stars(root).forEach((star, index) => {
      const value = index + 1;
      star.classList.toggle('is-filled', rating !== null && value <= rating);
      star.setAttribute('aria-checked', rating !== null && value === rating ? 'true' : 'false');
      // Roving tabindex: the checked radio (or the first, unrated) is tabbable.
      const tabbable = rating !== null ? value === rating : value === 1;
      star.setAttribute('tabindex', tabbable ? '0' : '-1');
    });
    if (rating !== null) {
      root.classList.add('fs-rr--rated');
      setStatus(root, 'rated', COPY.rated(rating));
    }
  }

  function setBusy(root, busy) {
    root.classList.toggle('fs-rr--busy', busy);
    root.dataset.fsRrBusy = busy ? '1' : '';
    stars(root).forEach((star) => {
      star.disabled = busy;
    });
  }

  /* -------------------------------------------------------------- wire */

  function submitRating(root, rating) {
    const handle = root.dataset.handle;
    const companyId = root.dataset.companyId;
    if (!handle) return;

    // Double-tap guard: one in-flight submission per widget.
    if (root.dataset.fsRrBusy === '1') return;

    const previous = getStoredRating(handle);

    // Same-value re-tap: the stored value implies a previously dispatched
    // event — skip the redundant send, just re-confirm the UI.
    if (previous !== null && previous === rating) {
      reflectRating(root, rating);
      return;
    }

    // PRIMARY: native track through Klaviyo's own queue — the event rides
    // the visitor's own profile. Fire-and-forget (no response available),
    // so treat dispatch as success. Synchronous throw → fall to the POST.
    const queue = nativeKlaviyoQueue();
    if (queue) {
      try {
        queue.push(['track', METRIC_NAME, eventProperties(handle, rating)]);
        setStoredRating(handle, rating);
        reflectRating(root, rating);
        return;
      } catch (error) {
        /* fall through to the raw-POST fallback */
      }
    }

    // FALLBACK: raw POST to the client Events API (service profile).
    if (!companyId || !window.fetch) {
      setStatus(root, 'error', COPY.failure);
      return;
    }

    setBusy(root, true);
    setStatus(root, '', '');
    reflectRating(root, rating); // optimistic fill; reverted on failure

    // credentials:'omit' — hard guarantee that no cookie (Klaviyo's or
    // anyone's) accompanies this cross-origin request. See header comment.
    fetch(KLAVIYO_EVENTS_ENDPOINT + encodeURIComponent(companyId), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(buildFallbackPayload(handle, rating)),
    })
      .then((response) => {
        // Klaviyo accepts with 202 + empty body; any non-2xx (rejected
        // payload, rate limit, outage) lands in catch — the rating is NOT
        // stored locally, so the visitor can simply tap again.
        if (!response.ok) throw new Error('Klaviyo events responded ' + response.status);
        setStoredRating(handle, rating);
        reflectRating(root, rating);
      })
      .catch(() => {
        reflectRating(root, previous); // roll back the optimistic fill
        setStatus(root, 'error', COPY.failure);
      })
      .finally(() => {
        setBusy(root, false);
      });
  }

  function wireStars(root) {
    const group = root.querySelector('[data-fs-rr-stars]');
    if (!group) return;
    const radios = stars(root);

    radios.forEach((star, index) => {
      star.addEventListener('click', () => {
        const rating = Number(star.dataset.rating);
        if (rating >= 1 && rating <= 5) submitRating(root, rating);
      });

      // Radiogroup keyboard contract: arrows move focus between radios
      // (roving tabindex); Enter/Space commits via native button click.
      star.addEventListener('keydown', (event) => {
        let target = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          target = radios[(index + 1) % radios.length];
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          target = radios[(index - 1 + radios.length) % radios.length];
        } else if (event.key === 'Home') {
          target = radios[0];
        } else if (event.key === 'End') {
          target = radios[radios.length - 1];
        }
        if (target) {
          event.preventDefault();
          radios.forEach((r) => r.setAttribute('tabindex', r === target ? '0' : '-1'));
          target.focus();
        }
      });
    });
  }

  function init() {
    const roots = document.querySelectorAll('[data-fs-recipe-rating]');
    if (!roots.length) return;

    roots.forEach((root) => {
      root.classList.add('fs-rr--enhanced');
      wireStars(root);
      const stored = getStoredRating(root.dataset.handle || '');
      if (stored !== null) reflectRating(root, stored);
    });
  }

  if (READY_STATES.includes(document.readyState)) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
